import { Dispatch, SetStateAction, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { SshKeyImportV3Body } from '@newfold/huapi-js/src/index.schemas';

import { ReactComponent as SshKeyIcon } from '~/assets/images/sshkey-icon.svg';
import ResponsiveDialog, {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';
import ProcessingState from '~/components/Status/ProcessingState';
import SuccessState from '~/components/Status/SuccessState';
import useAccount from '~/hooks/useAccount';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useImportKey from '~/hooks/useImportKey';
import ModalHeader from '~/Nova/scenes/HostingList/scenes/SelfManagedHosting/scenes/SelfManagedHostingLayout/AiTooling/components/ManageApiKeysModal/ModalHeader';
import { isValidUsernameWithOptionalDot } from '~/utils/username/validations';

import { SUPPORTED_KEY_FORMATS } from '../utils/constant';
import { isValidSSHPublicKey, removePublicKeyExtension } from '../utils/helper';

type AddPublicKeyPropOptions = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  defaultKeyName?: string;
  onSuccess?: () => void;
};

type AddPublicKeyFormValues = {
  name: string;
  pubkey: string;
};

type AddPublicKeyStatus = 'form' | 'processing' | 'success';

const EMPTY_FORM_VALUES: AddPublicKeyFormValues = {
  name: '',
  pubkey: '',
};

const AddPublicKey = ({
  open,
  setOpen,
  onSuccess = undefined,
}: AddPublicKeyPropOptions) => {
  const { t } = useTranslation('sshKeyManager', {
    keyPrefix: 'addSSHKey',
  });
  const { id: hostingId } = useAccount();
  const theme = useTheme();
  const { generateSSHPublicKeyUrl } = useBrandInfo();
  const [status, setStatus] = useState<AddPublicKeyStatus>('form');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<AddPublicKeyFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
    mode: 'all',
  });

  const { mutate: importKey, reset: resetImport } = useImportKey({
    onSuccess: () => {
      setStatus('success');
      onSuccess?.();
    },
    onError: () => {
      setStatus('form');
    },
  });

  const isProcessing = status === 'processing';
  const isSuccess = status === 'success';
  const isForm = status === 'form';

  const resetModalState = () => {
    reset(EMPTY_FORM_VALUES);
    resetImport();
    setStatus('form');
  };

  const closeModal = () => {
    resetModalState();
    setOpen(false);
  };

  const handleClose = () => {
    if (isProcessing) {
      return;
    }

    closeModal();
  };

  const handleSuccessClose = () => {
    if (isProcessing) {
      return;
    }

    closeModal();
  };

  const handleStatusClose = () => {
    if (!isProcessing) {
      handleSuccessClose();
    }
  };

  const onSubmit: SubmitHandler<AddPublicKeyFormValues> = ({
    name,
    pubkey,
  }) => {
    const trimmedPubkey = pubkey.trim();

    if (!trimmedPubkey) {
      return;
    }

    const payload: SshKeyImportV3Body = {
      name: name.endsWith('.pub') ? removePublicKeyExtension(name) : name,
      pubkey: trimmedPubkey,
    };

    setStatus('processing');

    importKey({
      hostingId,
      data: payload,
    });
  };

  const renderContent = () => {
    if (isProcessing) {
      return (
        <ProcessingState
          title={t('processingTitle')}
          description={t('processingDescription')}
        />
      );
    }

    if (isSuccess) {
      return (
        <SuccessState
          title={t('successTitle')}
          description={t('successDescription')}
        />
      );
    }

    return (
      <Stack spacing={1}>
        <Stack spacing={1}>
          <Typography variant="body2">{t('keyName')}</Typography>

          <TextField
            id="ssh-key-name"
            fullWidth
            size="small"
            required
            placeholder={t('keyNamePlaceholder')}
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name', {
              required: t('titleMandatoryTxt'),
              validate: (value) =>
                isValidUsernameWithOptionalDot(value) ||
                t('validateUserTitleText'),
            })}
          />
        </Stack>

        <Stack spacing={0.75}>
          <Typography variant="body2">{t('sshPublicKey')}</Typography>

          <TextField
            id="ssh-public-key"
            fullWidth
            multiline
            rows={5}
            required
            placeholder={t('publicKeyPlaceholder')}
            sx={{
              '& .MuiInputBase-input::placeholder': {
                typography: 'body2',
              },
            }}
            error={Boolean(errors.pubkey)}
            helperText={errors.pubkey?.message}
            {...register('pubkey', {
              required: t('publicMandatoryTxt'),
              validate: (value) => {
                const trimmedValue = value?.trim();

                if (!trimmedValue) {
                  return t('publicMandatoryTxt');
                }

                return (
                  isValidSSHPublicKey(trimmedValue) || t('invalidPublicKey')
                );
              },
            })}
          />

          <Stack spacing={1}>
            <Typography variant="caption">{t('supportedFormats')}</Typography>
            <Typography variant="caption">{SUPPORTED_KEY_FORMATS}</Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  };

  return (
    <ResponsiveDialog open={open} data-testid="ssh-add-key-dialog">
      <ModalHeader
        icon={<SshKeyIcon />}
        title={isSuccess ? t('successHeader') : t('addPublicKey')}
        subtitle={isForm ? t('generateKeyDesc') : undefined}
        learnMoreText={isForm ? t('generateKeyLearnMoreLinkText') : undefined}
        learnMoreHref={isForm ? generateSSHPublicKeyUrl : undefined}
        onClose={isProcessing ? undefined : handleClose}
      />

      <DialogContent>
        <Divider sx={{ mb: 2 }} />
        {renderContent()}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: theme.palette.background.lightGrey,
        }}
      >
        {isForm ? (
          <>
            <StyledButton variant="outlined" onClick={handleClose}>
              {t('cancelTxt')}
            </StyledButton>

            <StyledLoadingButton
              variant="contained"
              disabled={!isDirty || !isValid}
              onClick={handleSubmit(onSubmit)}
            >
              {t('saveTxt')}
            </StyledLoadingButton>
          </>
        ) : (
          <StyledButton
            variant="contained"
            onClick={handleStatusClose}
            disabled={isProcessing}
            data-testid="add-public-key-close-btn"
          >
            {t('btnTxt')}
          </StyledButton>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default AddPublicKey;
